import styled, { css } from 'styled-components';
import {
  FONT_SIZE_12,
  FONT_SIZE_14,
  FONT_SIZE_16,
  FONT_SIZE_24,
} from '@nokia-csf-uxr/freeform-design-tokens/tokens/fontSize';
import { BODY } from '@nokia-csf-uxr/freeform-design-tokens/tokens/typography';
import { LINE_HEIGHT_NONE } from '@nokia-csf-uxr/freeform-design-tokens/tokens/lineHeight';
import { RADIUS_CIRCLE, RADIUS_4 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/border-radius';
import { SPACING_2 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/spacing';
import { TRANSPARENT } from '@nokia-csf-uxr/freeform-design-tokens/tokens/colors';
import { zIndex } from 'styles';
import { BORDER_BOX } from 'common';


const SMALL_SIZE = 1.25;
const MEDIUM_SIZE = 2;
const LARGE_SIZE = 2.5;
const XLARGE_SIZE = 3;

const SMALL_FONT = FONT_SIZE_12;
const MEDIUM_FONT = FONT_SIZE_14;
const LARGE_FONT = FONT_SIZE_16;
const XLARGE_FONT = FONT_SIZE_24;

const AVATAR_DISABLED = css`
  ${({ disabled }) => disabled && css`
    cursor: default;
    pointer-events: none;
    background-color: ${props => props.theme.disabled.background};
    color: ${props => props.theme.disabled.content};
    & > div > div > div > div > svg * {
      fill: ${props => props.theme.disabled.icon};
    }
    // hexagon image disabled
    & > div > svg * {
      ${({ isImg }) => isImg && css`
        fill: ${props => props.theme.disabled.imageBackground};
      `}
    }
    &:focus {
      border: ${SPACING_2} solid ${TRANSPARENT};
    }
  `}
`;

const AVATAR_PRESSED = css`
  ${({ hoverEffect, pressed }) => hoverEffect && pressed && css`
    background-color: ${props => props.theme.pressed.background};
    ${({ shape }) => (shape === 'hexagon') && css`
      background-color: ${props => props.theme.pressed.background};
    `}
  `}; 
`;

const AVATAR_HOVERED_FOCUSED = css`
  ${({ hoverEffect }) => hoverEffect && css`
    &:focus {
      ${({ disabled }) => !disabled && css`
      ${({ isImg }) => !isImg && css`
        box-shadow: 0 0 0 ${SPACING_2} ${props => props.theme.focused.border};
      `}
      ${({ isImg }) => isImg && css`
        & > img {
          outline: none;
          box-shadow: 0 0 0 ${SPACING_2} ${props => props.theme.focused.border};
        `}
      }
      `}
    }
    &:hover {
      ${({ disabled }) => !disabled && css`
        ${({ isImg }) => !isImg && css`
          background-color: ${props => props.theme.hovered.background};
          ${({ pressed }) => pressed && css`
            ${AVATAR_PRESSED}
          `}; 
        `}
      `}
    }
  `}; 
`;

const GET_BORDER_RADIUS = props =>
  props.shape === 'square' ? RADIUS_4
    : props.shape === 'circle' ? RADIUS_CIRCLE
      : RADIUS_4;

const GET_FONT_SIZE = props =>
  props.diameter ? `calc(${props.diameter} * 0.45);`
    : props.size === 'small' ? SMALL_FONT
      : props.size === 'large' ? LARGE_FONT
        : props.size === 'xlarge' ? XLARGE_FONT
          : MEDIUM_FONT; // default size

const AVATAR_TYPOGRAPHY = css`
  ${BODY}
  line-height: ${LINE_HEIGHT_NONE};
  font-size: ${props => `${GET_FONT_SIZE(props)}`};
`;

const getDimensions = ({ size }) => {
  let avatarHeight;
  switch (size) {
    case 'small':
      avatarHeight = SMALL_SIZE;
      break;
    case 'large':
      avatarHeight = LARGE_SIZE;
      break;
    case 'xlarge':
      avatarHeight = XLARGE_SIZE;
      break;
    default:
      avatarHeight = MEDIUM_SIZE;
      break;
  }
  const avatarWidth = `${avatarHeight}rem`;
  avatarHeight = `${avatarHeight}rem`;
  return css`
      height: ${avatarHeight};
      width: ${avatarWidth};
      min-height: ${avatarHeight};
      min-width: ${avatarWidth};
      max-height: ${avatarHeight};
      max-width: ${avatarWidth};
    `;
};

// for backward compatiblity
const getDiameter = diameter => ({
  height: `${diameter}`,
  width: `${diameter}`,
  'min-height': `${diameter}`,
  'min-width': `${diameter}`,
  'max-height': `${diameter}`,
  'max-width': `${diameter}`,
});

const getBadgePlacement = ({ size, shape, RTL, $iconBadge }) => {
  let leftPlacement;
  let topPlacement;
  switch (size) {
    case 'medium':
      switch (shape) {
        case 'circle':
          leftPlacement = '1.25rem';
          topPlacement = '1.25rem';
          if ($iconBadge) {
            leftPlacement = '1.15rem';
            topPlacement = '1.15rem';
          }
          break;
        case 'square':
          leftPlacement = '1.5rem';
          topPlacement = '1.3rem';
          break;
        case 'hexagon':
          leftPlacement = '1.5625rem';
          topPlacement = '1.3125rem';
          if ($iconBadge) {
            topPlacement = '1.5rem';
          }
          break;
        default:
          break;
      }
      break;
    case 'large':
      switch (shape) {
        case 'circle':
          leftPlacement = '1.74rem';
          topPlacement = '1.7625rem';
          if ($iconBadge) {
            leftPlacement = '1.625rem';
            topPlacement = '1.625rem';
          }
          break;
        case 'square':
          leftPlacement = '2rem';
          topPlacement = '1.75rem';
          break;
        case 'hexagon':
          leftPlacement = '1.75rem';
          topPlacement = '1.75rem';
          if ($iconBadge) {
            topPlacement = '2rem';
          }
          break;
        default:
          break;
      }
      break;
    case 'xlarge':
      switch (shape) {
        case 'circle':
          leftPlacement = '2.25rem';
          topPlacement = '1.85rem';
          if ($iconBadge) {
            leftPlacement = '2.15rem';
            topPlacement = '2.15rem';
          }
          break;
        case 'square':
          leftPlacement = '2.5rem';
          topPlacement = '1.75rem';
          if ($iconBadge) {
            leftPlacement = '2.25rem';
            topPlacement = '2.25rem';
          }
          break;
        case 'hexagon':
          leftPlacement = '2.25rem';
          topPlacement = '2.25rem';
          if ($iconBadge) {
            topPlacement = '2.5rem';
          }
          break;
        default:
          break;
      }
      break;
    default:
      leftPlacement = undefined;
      topPlacement = undefined;
      break;
  }
  if (RTL) {
    return css`
    right: ${leftPlacement};
    top: ${topPlacement};
  `;
  }
  return css`
    left: ${leftPlacement};
    top: ${topPlacement};
  `;
};

const Wrapper = styled.span`
  position: relative;
  ${BORDER_BOX}
  cursor: pointer;
  ${({ hoverEffect }) => !hoverEffect && css`
    cursor: default;
  `}
  ${props => css`direction: ${(props.RTL || props.$rtl || props.rtl) ? 'rtl' : 'ltr'};`}
  border-radius: ${props => `${GET_BORDER_RADIUS(props)}`};
  ${({ isImg }) => !isImg && css`
    background-color: ${props => props.theme.container};
    box-shadow: 0 0 0 ${SPACING_2} ${TRANSPARENT};
  `}
  color: ${props => props.theme.content};
  ${props => getDimensions(props)};
  ${props => props.diameter !== undefined && getDiameter(props.diameter)};
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  & > div > div > div > div > svg * {
    ${({ disabled }) => !disabled && css`
      fill: ${props => props.theme.content};
      pointer-events: auto;
    `}
  }
  & > img {
    height: 100%;
    width: 100%;
    ${props => props.diameter !== undefined && getDiameter(props.diameter)};
    background-color: ${TRANSPARENT};
    border-radius: ${props => `${GET_BORDER_RADIUS(props)}`};
    ${({ hoverEffect }) => hoverEffect && css`
      box-shadow: 0 0 0 ${SPACING_2} ${TRANSPARENT};
    `}
  }
  ${AVATAR_TYPOGRAPHY}
  ${AVATAR_HOVERED_FOCUSED}
  ${AVATAR_DISABLED}
  ${AVATAR_PRESSED}
  &:focus-visible {
    outline: none;
  }; 
`;

const HexWrapper = styled.div`
  position: relative;
  width: ${props => props.width};
  height: ${props => props.height};
`;

const HexOutside = styled.div`
  position: relative;
  width: ${props => props.width};
  height: ${props => props.height};
  & > div > svg * {
    ${({ isImg }) => !isImg && css`
      fill: ${props => props.theme.container};
    `}
    pointer-events: auto;
    ${({ hoverEffect, hovered }) => hoverEffect && hovered && css`
      fill: ${props => props.theme.hovered.background};
      ${({ isImg }) => !isImg && css`
          fill: ${props => props.theme.hovered.background};
      `}
    `}
    ${({ hoverEffect, pressed }) => hoverEffect && pressed && css`
      fill: ${props => props.theme.pressed.background};
    `}
    ${({ disabled }) => disabled && css`
      fill: ${props => props.theme.disabled.background};
      ${({ isImg }) => !isImg && css`
        background-color: ${props => props.theme.disabled.background};
      `}
    `}
  }
  &:focus {
    outline: none;
    border: none;
  }
  &:focus-visible {
    outline: none;
  }; 
`;

const HexInside = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  cursor: default;
  ${({ disabled, hoverEffect }) => !disabled && hoverEffect && css`
    cursor: pointer;
  `}
  pointer-events: auto;
  z-index: 1;
  color: ${props => props.theme.content};
  width: ${props => props.width};
  height: ${props => props.height};
  ${({ isImg }) => isImg && css`
    clip-path: polygon(0% 50%, 25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100% );
  `}
  ${AVATAR_TYPOGRAPHY}
  transition: .5s ease;
  svg * {
    ${({ isImg }) => !isImg && css`
      fill: ${props => props.theme.content};
    `}
    ${({ isImg, hoverEffect }) => isImg && hoverEffect && css`
      ${({ hovered }) => hovered && css`
        fill: ${props => props.theme.hovered.imageBackground};
      `}
      ${({ pressed }) => pressed && css`
        fill: ${props => props.theme.pressed.imageBackground};
      `}
    `}
  }
  ${AVATAR_DISABLED}
  & > img {
    position: absolute;
    width: 100%; 
    height: 100%; 
    object-fit: cover;
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  ${props => getDimensions(props)};
  transition: .5s ease;
  ${({ isImg }) => isImg && css`
    background-color: ${props => props.theme.hovered.imageBackground};
  `}
  opacity: 0;
  border-radius: ${props => `${GET_BORDER_RADIUS(props)}`}; 
  &:hover {
    ${({ hoverEffect }) => hoverEffect && css`
      opacity: 1;
    `}
  }
  ${({ hoverEffect, pressed }) => hoverEffect && pressed && css`
      opacity: 1;
      background-color: ${props => props.theme.pressed.imageBackground};
  `}
  ${({ disabled }) => disabled && css`
    opacity: 1;
    background-color: ${props => props.theme.disabled.imageBackground};
    ${({ isImg }) => !isImg && css`
      z-index: -1; // put underneath Icon/text
    `}
  `}
`;

const BadgeWrapper = styled.div`
  position: absolute;
  ${props => getBadgePlacement(props)};
  pointer-events: none;
  z-index: ${zIndex.BADGE};
  ${({ RTL }) => RTL && css`
    direction: rtl;
  `}
`;

const styles = {
  Wrapper,
  HexWrapper,
  HexOutside,
  HexInside,
  ImageOverlay,
  BadgeWrapper,
};

export default styles;